import { ReactNode } from 'react';
import { Popup } from 'react-map-gl';
import { Link } from 'react-router-dom';

import { format0, format2, format8 } from 'utils/number';
import style from './map.module.scss';
import { PopupState } from './types';
import { getAreaHref } from './utils';
import { WellPopup } from './well-popup';
import { TitleEntityAssetsPopup } from './title-entity-assets-popup';
import { TypeCurveAreaPopup } from './type-curve-area-popup';
import { PipelinePopup } from './pipeline-popup';
import { DrillingRigPopup } from './drilling-rigs/drilling-rig-popup';

interface Props {
  popup: PopupState;
  locations: Array<{ id: number; name: string; quarterCalls?: string[] }>;
  onClose: () => void;
  showPopupOnSingleDevArea?: boolean;
  /** Passed through to the well popup so it can read the landing-analysis
   *  Landing Zone for this collection. Undefined on maps with no collection in
   *  scope, where the popup keeps showing the vendor value. */
  structureGridCollectionId?: number | null;
}

const MapPopup = ({
  popup,
  locations,
  onClose,
  structureGridCollectionId,
}: Props) => {
  if (!popup.visible) {
    return null;
  }

  if (popup.data?.type === 'wells') {
    return (
      <WellPopup
        closeButton={!!popup.pinned}
        onClose={onClose}
        latitude={popup.latitude}
        longitude={popup.longitude}
        wells={popup.data.value}
        structureGridCollectionId={structureGridCollectionId}
      />
    );
  }

  if (popup.data?.type === 'type-curve-scoped-wells') {
    return (
      <WellPopup
        closeButton={!!popup.pinned}
        onClose={onClose}
        latitude={popup.latitude}
        longitude={popup.longitude}
        wells={popup.data.value.reduce(
          (acc, { uwi }) => {
            acc[uwi] = undefined;
            return acc;
          },
          {} as Record<string, undefined>
        )}
        structureGridCollectionId={structureGridCollectionId}
      />
    );
  }

  if (popup.data?.type === 'dca-wells') {
    return (
      <WellPopup
        closeButton={!!popup.pinned}
        onClose={onClose}
        latitude={popup.latitude}
        longitude={popup.longitude}
        wells={popup.data.value}
        structureGridCollectionId={structureGridCollectionId}
      />
    );
  }

  let content: ReactNode = null;

  switch (popup.data?.type) {
    case 'development-area-scoped-wells':
      content = (
        <>
          {popup.data.value.map(({ uwi, name, status, type }) => (
            <div key={`${uwi}${name}${status}${type}`}>
              <Link
                to={`/wells/${uwi}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {uwi}
              </Link>
              <p>{name}</p>
              <p key={status}>{status}</p>
            </div>
          ))}
        </>
      );
      break;

    case 'basin-tier':
      content = (
        <>
          {popup.data.value.map((el, i) => (
            <p key={`${el}-${i}`}>{el}</p>
          ))}
        </>
      );
      break;
    case 'development-areas': {
      content = (
        <>
          {popup.data.value.basinTier.map((el, i) => (
            <p key={`${el}-${i}`}>{el}</p>
          ))}
          {popup.data.value.devAreas.map(({ name }, i) => {
            const href = getAreaHref(name);
            return (
              <p key={`${name}-${i}`}>
                <Link to={href} target={'_blank'}>
                  {name}
                </Link>
              </p>
            );
          })}
        </>
      );
      break;
    }
    case 'title-entity-assets':
      if (popup.data.value.entityIds) {
        return (
          <TitleEntityAssetsPopup
            closeButton={!!popup.pinned}
            onClose={onClose}
            latitude={popup.latitude}
            longitude={popup.longitude}
            assets={popup.data.value.entityIds}
          />
        );
      }
      break;

    case 'type-curve-area':
      return (
        <TypeCurveAreaPopup
          closeButton={!!popup.pinned}
          onClose={onClose}
          latitude={popup.latitude}
          longitude={popup.longitude}
          areaName={popup.data.value.areaName}
          typeCurves={popup.data.value.typeCurves}
        />
      );
    case 'reference-locations':
      content = locations.length ? (
        <>
          {locations.map((referenceLocation, i) => {
            return (
              <p key={`${referenceLocation.id}-${i}`}>
                {referenceLocation.name}
                {referenceLocation.quarterCalls?.length
                  ? ` (${referenceLocation.quarterCalls.join(', ')})`
                  : null}
              </p>
            );
          })}
        </>
      ) : null;
      break;

    case 'pipeline':
      return (
        <PipelinePopup
          closeButton={!!popup.pinned}
          onClose={onClose}
          latitude={popup.latitude}
          longitude={popup.longitude}
          uspipId={popup.data.value.uspipId}
          pipelineName={popup.data.value.pipelineName}
        />
      );
    case 'drilling-rig':
      return (
        <DrillingRigPopup
          closeButton={!!popup.pinned}
          onClose={onClose}
          latitude={popup.latitude}
          longitude={popup.longitude}
          rigId={popup.data.value.rigId}
          uwi={popup.data.value.uwi}
        />
      );
    case 'portfolio-assets': {
      const pa = popup.data.value;
      const fmtDepth = (v: number | null) => format0(Number(v), '?');
      const depthStr =
        pa.startDepth !== null || pa.endDepth !== null
          ? `${fmtDepth(pa.startDepth)} – ${fmtDepth(pa.endDepth)}`
          : null;
      return (
        <Popup
          key={Number(popup.pinned)}
          closeButton={!!popup.pinned}
          closeOnClick={false}
          onClose={onClose}
          latitude={popup.latitude}
          longitude={popup.longitude}
          className={style.popup}
        >
          <div>
            <p>
              <strong>
                Asset:{' '}
                {pa.tractId ? (
                  <Link
                    to={`/crm/tracts_and_subtracts/${pa.tractId}/edit`}
                    target="_blank"
                  >
                    {pa.tractName}
                  </Link>
                ) : (
                  pa.tractName
                )}
              </strong>
            </p>
            <p>Entity: {pa.entityName}</p>
            {pa.interestType && <p>Interest Type: {pa.interestType}</p>}
            {pa.netAcres !== null && <p>Net Acres: {format8(pa.netAcres)}</p>}
            {pa.grossAcres !== null && (
              <p>Gross Acres: {format2(pa.grossAcres)}</p>
            )}
            <p>Depth Severance: {depthStr ?? '–'}</p>
          </div>
        </Popup>
      );
    }
    default:
      return null;
  }

  if (!content) {
    return null;
  }

  return (
    <Popup
      key={Number(popup.pinned)}
      closeButton={popup.pinned}
      closeOnClick={false}
      onClose={onClose}
      latitude={popup.latitude}
      longitude={popup.longitude}
      className={style.popup}
    >
      {content}
    </Popup>
  );
};

export default MapPopup;
